import { type CSSProperties } from "react";
import { SubagentElapsed } from "@/components/chat/SubagentElapsed";
import { SubagentStopButton } from "@/components/chat/SubagentStopButton";
import {
	isFailedSubagentStatus,
	hasMeasurableElapsed,
	subagentProfileLabel,
	subagentStatusText,
	subagentTotalInputTokens,
	type ChatSubagentRun,
	type SubagentRunStatus,
} from "@/lib/chat";
import { formatTokenCount } from "@/lib/format-datetime";
import { BADGE_STYLE, DANGER_BADGE_STYLE, NEUTRAL_BADGE_STYLE } from "@/lib/pane-styles";
import { ROW_HOVER_CLASS, ROW_SELECTED_CLASS } from "@/lib/row-styles";
const STATUS_BADGE: Record<SubagentRunStatus, CSSProperties> = {
	queued: NEUTRAL_BADGE_STYLE,
	running: { background: "var(--accent-subtle)", color: "var(--accent)" },
	cancelRequested: { background: "var(--warning-subtle)", color: "var(--warning)" },
	completed: NEUTRAL_BADGE_STYLE,
	failed: DANGER_BADGE_STYLE,
	cancelled: NEUTRAL_BADGE_STYLE,
	timedOut: DANGER_BADGE_STYLE,
};

export function SubagentTaskCard({
	run,
	selected,
	onSelect,
}: {
	run: ChatSubagentRun;
	selected: boolean;
	onSelect: () => void;
}) {
	return (
		// 停止ボタンはカード本体のボタンの外に置く。入れ子にするとクリックが選択へ伝播してしまう。
		<div
			data-testid="subagent-task-card"
			data-selected={selected ? "true" : "false"}
			className={`flex items-start ${selected ? ROW_SELECTED_CLASS : ROW_HOVER_CLASS}`}
		>
			<button
				type="button"
				onClick={onSelect}
				className="flex min-w-0 flex-1 flex-col gap-1 cursor-pointer border-0 bg-transparent text-left"
				style={{ padding: "8px 4px 8px 12px", color: "var(--fg-1)", fontFamily: "inherit" }}
			>
				<div className="flex min-w-0 items-center gap-2">
					<span style={{ ...BADGE_STYLE, ...STATUS_BADGE[run.status] }}>
						{subagentStatusText(run.status)}
					</span>
					<span
						title={run.description}
						data-tooltip-if-truncated
						className="min-w-0 flex-1 truncate text-caption"
					>
						{run.description}
					</span>
				</div>
				<div className="flex min-w-0 items-center gap-2 text-caption text-fg-2">
					<span>{subagentProfileLabel(run.profile)}</span>
					{run.isBackground && <span>バックグラウンド</span>}
					<TaskElapsed run={run} />
				</div>
				<div className="text-caption text-fg-2">
					入力 {formatTokenCount(subagentTotalInputTokens(run))} / 出力{" "}
					{formatTokenCount(run.outputTokens)} · {run.toolCallCount}ツール
				</div>
			</button>
			<div className="shrink-0 py-2 pr-2">
				<SubagentStopButton run={run} />
			</div>
		</div>
	);
}

/** 経過を測れない行 (終端したのに endedAt が無い) では何も出さない。 */
export function TaskElapsed({ run }: { run: ChatSubagentRun }) {
	if (!hasMeasurableElapsed(run)) return null;
	return (
		<span className={isFailedSubagentStatus(run.status) ? "text-danger" : undefined}>
			<SubagentElapsed run={run} />
		</span>
	);
}

/** 同じ選択状態を共有する run の並び。各セクションがカードの描画をこれに委ねる。 */
export function SubagentTaskCardList({
	runs,
	selectedId,
	onSelect,
}: {
	runs: ChatSubagentRun[];
	selectedId: string | null;
	onSelect: (id: string) => void;
}) {
	return (
		<>
			{runs.map((run) => (
				<SubagentTaskCard
					key={run.id}
					run={run}
					selected={run.id === selectedId}
					onSelect={() => onSelect(run.id)}
				/>
			))}
		</>
	);
}
